import React from 'react';
import { css as reactCss } from '@emotion/react';

import { colors } from '~src/main/theme/colors';
import { mediaQueries } from '~src/main/theme/media-queries';
import { LogoInlineWrapper } from '~src/main/logo/logo-inline-wrapper';
import { useGlobalState } from '~src/main/context/global-state';

interface ShippingCardProps extends React.HTMLAttributes<HTMLElement> {
  innerRef?: React.RefObject<HTMLDivElement>;
  logo: React.ReactElement;
  price: React.ReactElement;
  subtitle: React.ReactElement;
  title: string;
}

export const ShippingCard: React.FC<ShippingCardProps> = ({
  children,
  innerRef,
  logo,
  price,
  subtitle,
  title,
  ...props
}) => {
  const globalState = useGlobalState();

  return (
    <div
      aria-label={title}
      data-test="shipping-choice"
      className="focus-visible-outline"
      css={styles.wrapper}
      ref={innerRef}
      {...props}
    >
      <div css={styles.header(!!globalState?.flags?.show_carrier_icons)}>
        <div css={styles.title}>
          <div css={styles.titleContent} aria-hidden="true" data-test="shipping-choice-title">
            {title}
          </div>
          <div css={styles.subtitleContent}>{subtitle}</div>
        </div>
        <div css={styles.priceContent}>{price}</div>
        {globalState?.flags?.show_carrier_icons && <LogoInlineWrapper>{logo}</LogoInlineWrapper>}
      </div>
      <div css={styles.body}>{children}</div>
    </div>
  );
};

const styles = {
  wrapper: reactCss({
    padding: '18px 0px',
    borderTop: `1px solid ${colors.neutral90}`,
  }),
  header: (showCarrierLogos: boolean) =>
    reactCss({
      display: 'grid',
      position: 'relative',
      gridTemplateColumns: showCarrierLogos ? '1fr auto 32px' : '1fr auto 0',
      columnGap: showCarrierLogos ? '10px' : '0',
      color: 'black',
      fontSize: '16px',
      lineHeight: '20px',
      [mediaQueries.S]: {
        fontSize: '14px',
      },
    }),
  body: reactCss({
    marginTop: '6px',
    display: 'grid',
    rowGap: '6px',
    color: colors.neutral45,
    fontSize: '14px',
    lineHeight: '20px',
  }),
  title: reactCss({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  }),
  titleContent: reactCss({
    marginBottom: '2px', // ui hack: header should have an additional 2px margin.
    cursor: 'inherit',
    fontVariationSettings: '"wght" 500',
  }),
  subtitleContent: reactCss({
    color: colors.neutral45,
    fontSize: '14px',
    lineHeight: '20px',
    cursor: 'inherit',
  }),
  priceContent: reactCss({
    fontVariationSettings: '"wght" 500',
  }),
};
